import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { auth, db, googleProvider } from "../libs/firebaseConfig";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  sendEmailVerification,
  reload,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";


const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // Firebase Auth user
  const [profile, setProfile] = useState(null); // users/{uid} document
  const [loading, setLoading] = useState(true);
  const recaptchaRef = useRef(null);

  // --------------------------------------------------------------------------
  // ✅ Sync emailVerified ลง Firestore
  const syncEmailVerified = async (firebaseUser) => {
    if (!firebaseUser) return;
    const ref = doc(db, "users", firebaseUser.uid);
    await setDoc(
      ref,
      {
        emailVerified: !!firebaseUser.emailVerified,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  // --------------------------------------------------------------------------
  // ✅ สร้าง/อัปเดต users/{uid} (ensureUserDoc)
  const ensureUserDoc = async (fbUser, overrides = {}) => {
    const ref = doc(db, "users", fbUser.uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      const baseDoc = {
        uid: fbUser.uid,
        email: fbUser.email ?? "",
        emailVerified: !!fbUser.emailVerified,
        displayName: fbUser.displayName ?? "",
        photoURL: fbUser.photoURL ?? "",
        avatarPath: "",
        bio: "",
        website: "",
        twitter: "",
        facebook: "",
        instagram: "",
        tiktok: "",
        youtube: "",
        linkedin: "",
        provider: fbUser.providerData?.[0]?.providerId ?? "password",
        phoneNumber: fbUser.phoneNumber ?? "",
        role: "user", // ค่าเริ่มต้น
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await setDoc(ref, { ...baseDoc, ...overrides });
      return { ...baseDoc, ...overrides };
    } else {
      const patch = {
        email: fbUser.email ?? "",
        emailVerified: !!fbUser.emailVerified,
        displayName: fbUser.displayName ?? snap.data().displayName ?? "",
        photoURL: fbUser.photoURL ?? snap.data().photoURL ?? "",
        provider: fbUser.providerData?.[0]?.providerId ?? "password",
        phoneNumber: fbUser.phoneNumber ?? "",
        updatedAt: serverTimestamp(),
      };
      /* const patch = {
        email: fbUser.email ?? "",
        emailVerified: !!fbUser.emailVerified,
        displayName: fbUser.displayName ?? snap.data().displayName ?? "",
        provider: fbUser.providerData?.[0]?.providerId ?? "password",
        phoneNumber: fbUser.phoneNumber ?? "",
        updatedAt: serverTimestamp(),
      }; */
      await setDoc(ref, { ...patch, ...overrides }, { merge: true });
      return { ...snap.data(), ...patch, ...overrides };
    }
  };

  // --------------------------------------------------------------------------
  // ✅ reload user หลัง verify email
  const refreshUser = async () => {
    if (!auth.currentUser) return;
    await reload(auth.currentUser);
    setUser({ ...auth.currentUser });
    await syncEmailVerified(auth.currentUser);
  };

  // --------------------------------------------------------------------------
  // ✅ Observer (Auth + Firestore)
  useEffect(() => {
    let unsubProfile = null;

    const unsubAuth = onAuthStateChanged(auth, async (fbUser) => {
      setLoading(true);
      try {
        if (fbUser) {
          setUser(fbUser);
          /* await ensureUserDoc(fbUser); 
          await syncEmailVerified(fbUser); */
          await ensureUserDoc(fbUser); // ให้ฟังก์ชันนี้ดูแลการสร้าง/อัปเดตหลัก

          const ref = doc(db, "users", fbUser.uid);
          unsubProfile = onSnapshot(ref, (snap) => {
            if (snap.exists()) {
              setProfile(snap.data());
            } else {
              setProfile(null);
            }
          });
        } else {
          setUser(null);
          setProfile(null);
          if (unsubProfile) unsubProfile();
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  // --------------------------------------------------------------------------
  // ✅ Actions
  const registerEmail = async (email, password, displayName) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);

    if (displayName) await updateProfile(user, { displayName });

    await ensureUserDoc(user, {
      displayName: displayName || user.displayName || "",
      email,
      emailVerified: !!user.emailVerified,
    });

    await sendEmailVerification(user, {
      url: "https://encraft.org", // เปลี่ยนเป็น URL จริงตอน deploy
    });

    return user;
  };

  const loginEmail = async (email, password) => {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    await ensureUserDoc(user);
    return user;
  };

  const loginGoogle = async () => {
    const { user } = await signInWithPopup(auth, googleProvider);
    await ensureUserDoc(user);
    return user;
  };

  // Phone Auth
  const sendOtp = async (phoneNumber, { invisible = true } = {}) => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: invisible ? "invisible" : "normal",
      });
    }
    const confirmation = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      recaptchaRef.current
    );
    return confirmation;
  };

  const verifyOtp = async (confirmationResult, code) => {
    const { user } = await confirmationResult.confirm(code);
    await ensureUserDoc(user);
    return user;
  };

  const logout = () => signOut(auth);

  // --------------------------------------------------------------------------
  // ✅ Context value
  const value = useMemo(
    () => ({
      user,
      profile,         // เอกสาร Firestore ของผู้ใช้
      userDoc: profile, // alias (ให้โค้ดอื่นที่ใช้เดิมไม่พัง)
      loading,
      // actions
      registerEmail,
      loginEmail,
      loginGoogle,
      sendOtp,
      verifyOtp,
      logout,
      // helpers
      refreshUser,
    }),
    [user, profile, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {/* สำหรับ Phone Auth */}
      <div id="recaptcha-container" style={{ display: "none" }} />
      {children}
    </AuthContext.Provider>
  );
}

// --------------------------------------------------------------------------
export const useAuth = () => useContext(AuthContext);
export const useAuthContext = () => useContext(AuthContext);


